import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Briefcase, Globe, Link2, ThumbsUp, ThumbsDown, Copy, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIALIZATION_OPTIONS = ['Technical Recruiter', 'Executive Search', 'Talent Acquisition', 'Campus Hiring', 'Healthcare Recruiting', 'Sales Recruiting', 'Finance Recruiting', 'Volume Hiring', 'Leadership Hiring', 'DevOps', 'Data & AI', 'Product & Design'];
const INDUSTRY_OPTIONS = ['SaaS', 'FinTech', 'Healthcare', 'E-commerce', 'Manufacturing', 'Consulting', 'Staffing', 'Banking', 'Startups', 'Government'];
const EMPLOYMENT_OPTIONS = ['full-time', 'contract', 'part-time', 'freelance'];

const inputCls = 'w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-400 outline-none';

const TagPicker = ({ options, value = [], onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(opt => {
      const active = value.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          data-testid={`tag-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          onClick={() => onChange(active ? value.filter(v => v !== opt) : [...value, opt])}
          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
            active
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white border-transparent shadow-md shadow-blue-500/30'
              : 'bg-slate-100 border-slate-300 text-slate-700 hover:border-blue-400'
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase font-bold tracking-[0.15em] text-slate-500 mb-2">{label}</label>
    {children}
  </div>
);

export const Career360Section = ({ onSaved }) => {
  const { token } = useAuth();
  const [form, setForm] = useState({});
  const [completeness, setCompleteness] = useState(0);
  const [handle, setHandle] = useState('');
  const [handleInput, setHandleInput] = useState('');
  const [pendingRecs, setPendingRecs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [founding, setFounding] = useState(null);
  const [foundingStats, setFoundingStats] = useState(null);
  const [copied, setCopied] = useState(false);

  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/recruiter/profile360`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setForm(d.profile360 || {});
          setCompleteness(d.completeness);
          setHandle(d.handle);
          setHandleInput(d.handle);
          setFounding(d.is_founding_member ? d.founding_member_number : null);
        }
      });
    fetch(`${API}/api/recruiter/founding-status`)
      .then(r => r.json())
      .then(d => d.success && setFoundingStats(d));
    fetch(`${API}/api/recruiter/recommendations/pending`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => d.success && setPendingRecs(d.pending));
  }, [token]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/recruiter/profile360`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(form)
      });
      const d = await res.json();
      if (d.success) {
        setCompleteness(d.completeness);
        if (d.is_founding_member && !founding) {
          setFounding(d.founding_member_number);
          toast.success(`🚀 Congratulations! You are Founding Member #${d.founding_member_number}!`, { duration: 8000 });
        } else {
          toast.success(`Career profile saved — ${d.completeness}% complete`);
        }
        onSaved && onSaved();
      } else toast.error(d.detail || 'Save failed');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const saveHandle = async () => {
    const res = await fetch(`${API}/api/recruiter/handle`, {
      method: 'POST', headers: authHeaders, body: JSON.stringify({ handle: handleInput })
    });
    const d = await res.json();
    if (d.success) { setHandle(d.handle); toast.success('Your page URL updated!'); }
    else toast.error(d.detail || 'Handle unavailable');
  };

  const copySiteLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/r/${handle}`);
    setCopied(true);
    toast.success('Personal page link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const respondRec = async (recId, approve) => {
    await fetch(`${API}/api/recruiter/recommendations/${recId}/respond`, {
      method: 'POST', headers: authHeaders, body: JSON.stringify({ approve })
    });
    setPendingRecs(prev => prev.filter(r => r.rec_id !== recId));
    toast.success(approve ? 'Recommendation approved' : 'Recommendation declined');
  };

  return (
    <div className="space-y-6" data-testid="career-360-section">
      {/* Completeness */}
      <Card className="shadow-lg border-2 border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <CardContent className="pt-6 flex items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg text-slate-900">Career 360° Completeness</h3>
            <p className="text-sm text-slate-600">Powers your job matches and employer discovery.</p>
          </div>
          <div className="text-right shrink-0">
            <span data-testid="completeness-value" className="text-3xl font-bold text-blue-600">{completeness}%</span>
            <div className="w-36 h-2 rounded-full mt-2 bg-slate-200">
              <div className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all" style={{ width: `${completeness}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Founding Member */}
      {founding ? (
        <div data-testid="founding-member-badge" className="border border-purple-300 bg-purple-50 rounded-2xl p-5 flex items-center gap-3">
          <span className="text-2xl">🚀</span>
          <div>
            <p className="font-bold text-purple-700">You are Founding Member #{founding}</p>
            <p className="text-sm text-slate-600">Permanent badge on your one-page site — thank you for building RecruiterIQ early.</p>
          </div>
        </div>
      ) : foundingStats && foundingStats.remaining > 0 ? (
        <div data-testid="founding-campaign-banner" className="border border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-5">
          <p className="font-bold text-purple-700">🚀 Founding Member campaign — {foundingStats.remaining.toLocaleString()} of 1,000 spots left</p>
          <p className="text-sm text-slate-600 mt-1">Complete your profile (90%+) to claim a permanent Founding Member badge on your public page.</p>
        </div>
      ) : null}

      {/* Personal page URL */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Link2 className="w-5 h-5 text-cyan-600" /> Your one-page website</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <a data-testid="my-site-link" href={`/r/${handle}`} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold text-sm break-all">{window.location.origin}/r/{handle}</a>
            <button type="button" data-testid="copy-site-link" onClick={copySiteLink} className="p-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
            </button>
          </div>
          <div className="flex gap-3">
            <input data-testid="handle-input" value={handleInput} onChange={e => setHandleInput(e.target.value)} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300" />
            <button type="button" data-testid="handle-save-btn" onClick={saveHandle} className="px-5 py-2.5 rounded-xl border border-slate-300 font-semibold hover:bg-slate-50">Update URL</button>
          </div>
        </CardContent>
      </Card>

      {/* Open to work */}
      <Card className="shadow-lg">
        <CardContent className="pt-6 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2 text-slate-900"><Globe className="w-5 h-5 text-cyan-600" /> Open to opportunities</h3>
            <p className="text-sm text-slate-600 mt-1">When on, verified employers can find you in search and message you. Your salary expectations become visible to them.</p>
          </div>
          <Switch data-testid="open-to-work-toggle" checked={!!form.open_to_work} onCheckedChange={v => set('open_to_work', v)} />
        </CardContent>
      </Card>

      {/* Fields */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg">Career Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Field label="Professional headline">
            <input data-testid="p360-headline" value={form.headline || ''} onChange={e => set('headline', e.target.value)} placeholder="e.g. Senior Technical Recruiter — scaling engineering teams globally" className={inputCls} />
          </Field>

          <Field label="What kind of roles do you source / fill?">
            <TagPicker options={SPECIALIZATION_OPTIONS} value={form.specializations || []} onChange={v => set('specializations', v)} />
          </Field>

          <Field label="Industries you support">
            <TagPicker options={INDUSTRY_OPTIONS} value={form.industries || []} onChange={v => set('industries', v)} />
          </Field>

          <Field label="Clients you have supported (comma separated)">
            <input data-testid="p360-clients" value={(form.clients || []).join(', ')} onChange={e => set('clients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="e.g. Fortune 500 tech, Series-B startups" className={inputCls} />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Recruiter type">
              <select data-testid="p360-recruiter-type" value={form.recruiter_type || ''} onChange={e => set('recruiter_type', e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                <option value="in-house">In-house / Corporate TA</option>
                <option value="agency">Agency</option>
                <option value="rpo">RPO</option>
                <option value="freelance">Freelance / Independent</option>
              </select>
            </Field>
            <Field label="Employment types you work">
              <TagPicker options={EMPLOYMENT_OPTIONS} value={form.employment_types || []} onChange={v => set('employment_types', v)} />
            </Field>
            <Field label="Country">
              <input data-testid="p360-country" value={form.work_country || ''} onChange={e => set('work_country', e.target.value)} placeholder="e.g. United States" className={inputCls} />
            </Field>
            <Field label="City">
              <input data-testid="p360-city" value={form.work_city || ''} onChange={e => set('work_city', e.target.value)} placeholder="e.g. Austin" className={inputCls} />
            </Field>
            <Field label="Work mode">
              <select data-testid="p360-work-mode" value={form.work_mode || ''} onChange={e => set('work_mode', e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                <option value="onsite">Onsite</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote</option>
              </select>
            </Field>
            <Field label="Years of experience">
              <input data-testid="p360-experience" type="number" value={form.years_experience || ''} onChange={e => set('years_experience', parseFloat(e.target.value) || null)} className={inputCls} />
            </Field>
            <Field label="Current salary (annual)">
              <input data-testid="p360-current-salary" value={form.current_salary || ''} onChange={e => set('current_salary', e.target.value)} placeholder="e.g. 75000" className={inputCls} />
            </Field>
            <Field label="Expected salary (annual)">
              <input data-testid="p360-expected-salary" value={form.expected_salary || ''} onChange={e => set('expected_salary', e.target.value)} placeholder="e.g. 90000" className={inputCls} />
            </Field>
            <Field label="Salary currency">
              <select data-testid="p360-currency" value={form.salary_currency || ''} onChange={e => set('salary_currency', e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Notice period">
              <input data-testid="p360-notice" value={form.notice_period || ''} onChange={e => set('notice_period', e.target.value)} placeholder="e.g. 30 days" className={inputCls} />
            </Field>
          </div>

          <div className="border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4 bg-slate-50">
            <div>
              <h3 className="font-bold flex items-center gap-2 text-slate-900"><Briefcase className="w-4 h-4 text-purple-600" /> Interested in part-time remote roles?</h3>
              <p className="text-sm text-slate-600">Side gigs, fractional TA work and consulting projects.</p>
            </div>
            <Switch data-testid="parttime-toggle" checked={!!form.open_to_parttime_remote} onCheckedChange={v => set('open_to_parttime_remote', v)} />
          </div>

          <button type="button" data-testid="p360-save-btn" onClick={save} disabled={saving} className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg hover:brightness-110 transition disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Career Profile'}
          </button>
        </CardContent>
      </Card>

      {/* Pending recommendations */}
      {pendingRecs.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Recommendations awaiting your approval</CardTitle>
          </CardHeader>
          <CardContent>
            {pendingRecs.map(rec => (
              <div key={rec.rec_id} className="border border-slate-200 rounded-xl p-4 mb-3">
                <p className="text-sm mb-2">"{rec.text}"</p>
                <p className="text-xs text-slate-500 mb-3">— {rec.from_name}{rec.from_headline ? `, ${rec.from_headline}` : ''}</p>
                <div className="flex gap-2">
                  <button type="button" data-testid={`approve-rec-${rec.rec_id}`} onClick={() => respondRec(rec.rec_id, true)} className="px-4 py-1.5 rounded-lg bg-emerald-500 text-white text-sm font-semibold flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> Approve</button>
                  <button type="button" data-testid={`decline-rec-${rec.rec_id}`} onClick={() => respondRec(rec.rec_id, false)} className="px-4 py-1.5 rounded-lg border border-slate-300 text-sm flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> Decline</button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Career360Section;
